import type { EninCompanySearchHit } from "@repo/shared/types";
import { Button } from "@/components/ui/button";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/i18n/use-t";
import type { ComponentStyleType } from "@/lib/types/flow";
import { T } from "./enin-lookup-picker.i18n";

type EninLookupPickerProps = {
  selectedCompany: { companyId: string; companyName: string | null } | null;
  onRun: () => void;
  onClearSelection: () => void;
  query: string;
  setQuery: (value: string) => void;
  onSearch: () => void;
  searchRows: EninCompanySearchHit[] | null;
  onPick: (hit: EninCompanySearchHit) => void;
  searchPending: boolean;
  searchError: string | null;
  error: string | null;
  isPending: boolean;
  disabled: boolean;
  canCancel: boolean;
  onCancel: () => void;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  hasValidationError?: boolean;
};

export function EninLookupPicker({
  selectedCompany,
  onRun,
  onClearSelection,
  query,
  setQuery,
  onSearch,
  searchRows,
  onPick,
  searchPending,
  searchError,
  error,
  isPending,
  disabled,
  canCancel,
  onCancel,
  style = "none",
  title,
  description,
  hasValidationError = false,
}: EninLookupPickerProps) {
  const t = useT(T);
  const getContainerClass = () => {
    if (hasValidationError) {
      return "border border-destructive rounded-lg p-6 space-y-4";
    }
    switch (style) {
      case "border":
        return "border rounded-lg p-6 space-y-4";
      case "box":
        return "rounded-lg p-6 space-y-4 bg-muted/50";
      default:
        return "space-y-4";
    }
  };

  return (
    <div className={getContainerClass()}>
      <FlowComponentHeader title={title} description={description} />
      {hasValidationError && <p className="text-sm text-destructive">{t.incompleteMessage}</p>}

      <div className="space-y-4">
        {error && (
          <div className="p-3 rounded-md text-sm bg-destructive/10 text-destructive border border-destructive/30">
            {error}
          </div>
        )}

        {selectedCompany ? (
          <>
            {/* Confirm step — show which company will be looked up */}
            <div className="space-y-2">
              <Label>{t.companyToLookUp}</Label>
              <div className="flex flex-wrap items-center gap-2 rounded-md border p-3">
                <span className="font-medium">
                  {selectedCompany.companyName || selectedCompany.companyId}
                </span>
                <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                  {selectedCompany.companyId}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button
                type="button"
                variant="link"
                size="sm"
                className="px-0"
                onClick={onClearSelection}
                disabled={isPending || disabled}
              >
                {t.searchDifferentCompany}
              </Button>
              <div className="flex gap-2">
                {canCancel && (
                  <Button variant="outline" onClick={onCancel} disabled={isPending}>
                    {t.cancel}
                  </Button>
                )}
                <Button onClick={onRun} disabled={disabled} loading={isPending}>
                  {t.runLookup}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Name-search fallback */}
            <p className="text-sm text-muted-foreground">{t.autoResolveFailed}</p>
            <div className="space-y-2">
              <Label htmlFor="enin-search-name">{t.searchLabel}</Label>
              <div className="flex gap-2">
                <Input
                  id="enin-search-name"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onSearch();
                    }
                  }}
                  placeholder={t.searchPlaceholder}
                  disabled={searchPending || disabled}
                />
                <Button
                  onClick={onSearch}
                  disabled={disabled || query.trim().length < 2}
                  loading={searchPending}
                >
                  {t.search}
                </Button>
              </div>
            </div>

            {searchError && (
              <div className="p-3 rounded-md text-sm bg-destructive/10 text-destructive border border-destructive/30">
                {searchError}
              </div>
            )}

            {searchRows && searchRows.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t.noResults} "{query.trim()}"
              </p>
            )}

            {searchRows && searchRows.length > 0 && (
              <div className="space-y-2">
                {searchRows.map((hit) => (
                  <button
                    key={hit.companyId}
                    type="button"
                    onClick={() => onPick(hit)}
                    disabled={disabled}
                    className="w-full rounded-md border bg-background p-2 text-left text-sm hover:bg-muted/50"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium">{hit.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {hit.country} · {hit.orgNr}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {canCancel && (
              <div className="flex justify-end">
                <Button variant="outline" onClick={onCancel} disabled={isPending}>
                  {t.cancel}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
